import { useFlag } from '@afterpaytouch/feature'
import { Country, FirstPaymentType, RiskLevel } from '@afterpaytouch/portal-api'
import { OverduePaymentResponse, paymentScheduleService } from '@afterpaytouch/portal-api/consumer/paymentSchedule'
import { useCalculatedPaymentSchedules, useCheckoutState, useCountry, usePaymentOptionsHasPCL } from '..'
import { Features } from '../../model'
import { useAppRouter, useAppSelector } from '../hooks'
import { PaymentOptionsModal } from '../../component'
import { PaymentType } from '@afterpay/types'
import { Route } from '../../config/router'
import { create } from 'react-modal-promise'

/* Selectors */

// @TODO: Investigate if this select function requires memorisation for performance reasons
// This selector is specifically for the API state
export const overduePaymentsSelector = paymentScheduleService.endpoints.getOverduePayments.select()

/* React Hooks */
export const useOverduePaymentsCache = (): OverduePaymentResponse | undefined => useAppSelector(overduePaymentsSelector).data

export const useIsPayIn2 = (): boolean => {
  const paymentSchedules = useCalculatedPaymentSchedules()
  return paymentSchedules.length === 2
}

const AUTOPAY_CONTEXT_FOR_TOAST: Record<FirstPaymentType, string> = {
  [FirstPaymentType.NoPaymentUpfront]: '',
  [FirstPaymentType.VariablePaymentUpfront]: 'WithExceedLimit',
  [FirstPaymentType.LargePaymentUpfront]: 'WithExceedLimit',
  [FirstPaymentType.PaymentUpfront]: '',
  [FirstPaymentType.Unknown]: '',
} as const

const DOWN_PAYMENT_RISK_LEVEL_CONTEXT: Record<RiskLevel, string> = {
  LOW_RISK: 'lowerPayment',
  HIGH_RISK: 'higherPayment',
  RISK_LEVEL_UNKNOWN: '',
} as const

export const useAdjustedDownPaymentMessagingEnabled = (): boolean => {
  const { started } = useCheckoutState()
  const adjustedDownPaymentMessagingEnabled = useFlag(Features.ADJUSTED_DOWN_PAYMENT_MESSAGING, false)
  const eligible = started?.downPaymentRiskInfo?.adjustedDownPaymentEligible

  return adjustedDownPaymentMessagingEnabled && typeof eligible !== 'undefined' && eligible
}

export const useDownPaymentContext = (): string => {
  const { started } = useCheckoutState()
  const downPaymentRiskLevel = started?.downPaymentRiskInfo?.downPaymentRiskLevel ?? 'RISK_LEVEL_UNKNOWN'
  return DOWN_PAYMENT_RISK_LEVEL_CONTEXT[downPaymentRiskLevel] ?? ''
}

export const useToastContext = (): string => {
  const { firstPaymentType } = useCheckoutState()
  const autopayToggleIsEnabled = useFlag(Features.AUTOPAY_TOGGLE_ENABLED)

  const npufPaymentType = firstPaymentType === FirstPaymentType.NoPaymentUpfront
  const isStandardNPUFOrder = !autopayToggleIsEnabled && npufPaymentType

  if (isStandardNPUFOrder || (autopayToggleIsEnabled && npufPaymentType)) {
    return 'npuf'
  } else if (!autopayToggleIsEnabled) {
    return ''
  }

  const toastContext = AUTOPAY_CONTEXT_FOR_TOAST[firstPaymentType] ?? ''
  const autopayContext = `autopayToggleEnabled${toastContext}`
  return autopayContext
}

export const usePaymentMessageContext = (): string => {
  const { firstPaymentType, started } = useCheckoutState()
  const isPayIn2 = useIsPayIn2()
  const autopayToggleIsEnabled = useFlag(Features.AUTOPAY_TOGGLE_ENABLED)
  const adjustedDownPaymentMessagingEnabled = useAdjustedDownPaymentMessagingEnabled()

  const isNpufPaymentType = firstPaymentType === FirstPaymentType.NoPaymentUpfront
  const isVpufOrLpufPaymentType = firstPaymentType === FirstPaymentType.VariablePaymentUpfront || firstPaymentType === FirstPaymentType.LargePaymentUpfront

  const isStandardNpufOrder = !autopayToggleIsEnabled && isNpufPaymentType
  const isStandardExceedLimitOrder = !autopayToggleIsEnabled && isVpufOrLpufPaymentType
  const isStandardPayIn2Order = !autopayToggleIsEnabled && isPayIn2

  const isLowerDownPayment = adjustedDownPaymentMessagingEnabled && started?.downPaymentRiskInfo?.downPaymentRiskLevel === 'LOW_RISK'
  const isHigherDownPayment = adjustedDownPaymentMessagingEnabled && started?.downPaymentRiskInfo?.downPaymentRiskLevel === 'HIGH_RISK'

  const paymentMessageContextMap = [
    { predicate: isLowerDownPayment, context: 'lowerPayment' },
    { predicate: isHigherDownPayment, context: 'higherPayment' },
    { predicate: isStandardNpufOrder, context: '' },
    { predicate: isStandardExceedLimitOrder, context: 'exceedLimit' },
    { predicate: isStandardPayIn2Order, context: 'payIn2' },
    { predicate: !autopayToggleIsEnabled, context: '' },
  ]
  const defaultCase = 'autopayToggleEnabled'

  const match = paymentMessageContextMap.find((item) => item.predicate)
  return match?.context ?? defaultCase
}

export const useDisplayAutopayTooltipEnabled = (): boolean => {
  const autopayToggleIsEnabled = useFlag(Features.AUTOPAY_TOGGLE_ENABLED)
  const { firstPaymentType } = useCheckoutState()
  const npufPaymentType = firstPaymentType === FirstPaymentType.NoPaymentUpfront

  return autopayToggleIsEnabled && !npufPaymentType
}

export const useShowPaymentScheduleTooltip = (adjustedDownPaymentEnabled: boolean): boolean => {
  const country = useCountry()
  const { firstPaymentType } = useCheckoutState()
  const isFpuf = firstPaymentType === FirstPaymentType.PaymentUpfront
  const vpufOrLpufPaymentType = firstPaymentType === FirstPaymentType.VariablePaymentUpfront || firstPaymentType === FirstPaymentType.LargePaymentUpfront
  const isPayIn2 = useIsPayIn2()
  const displayAutopayTooltipEnabled = useDisplayAutopayTooltipEnabled()

  switch (country) {
    case Country.US:
      return displayAutopayTooltipEnabled || isFpuf || vpufOrLpufPaymentType || isPayIn2
    case Country.GB:
      return vpufOrLpufPaymentType || isPayIn2 || adjustedDownPaymentEnabled
    case Country.CA:
      return (vpufOrLpufPaymentType && !adjustedDownPaymentEnabled) || isPayIn2
    case Country.AU:
    case Country.NZ:
      return vpufOrLpufPaymentType || isPayIn2
    default:
      return false
  }
}

export const usePaymentPlanChangeHandler = (): (() => Promise<void>) => {
  const router = useAppRouter()
  const paymentOptionsHasPCL = usePaymentOptionsHasPCL()
  const showPaymentOptionsModal = create(PaymentOptionsModal)
  const handler = async (): Promise<void> => {
    if (paymentOptionsHasPCL) {
      const paymentType = await showPaymentOptionsModal()
      if (paymentType === PaymentType.PCL) {
        router.push(Route.CONSUMER_LENDING_SUMMARY)
      }
    } else {
      router.push(Route.PAYMENT_TYPES)
    }
  }

  return handler
}
